import React from 'react';

interface SparklineDataPoint {
  date: string;
  quantity: number;
  moving_average: number;
}

interface SparklineChartProps {
  data: SparklineDataPoint[];
  width?: number;
  height?: number;
}

const SparklineChart: React.FC<SparklineChartProps> = ({ 
  data, 
  width = 80, 
  height = 30 
}) => {
  if (!data || data.length < 2) {
    return (
      <div style={{ width, height }} className="d-flex align-items-center justify-content-center">
        <span className="text-muted small">N/A</span>
      </div>
    );
  }

  // Get min/max values for scaling
  const values = data.map(d => d.moving_average);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);
  const range = maxValue - minValue;

  // Avoid division by zero
  if (range === 0) {
    return (
      <div style={{ width, height }} className="d-flex align-items-center justify-content-center">
        <span className="text-muted small">—</span>
      </div>
    );
  }

  // Create SVG path
  const padding = 2;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;

  const points = data.map((point, index) => {
    const x = padding + (index / (data.length - 1)) * chartWidth;
    const y = padding + chartHeight - ((point.moving_average - minValue) / range) * chartHeight;
    return `${x},${y}`;
  });

  const pathData = `M ${points.join(' L ')}`;

  // Get first and last values for the dots
  const firstValue = values[0];
  const lastValue = values[values.length - 1];
  
  // Use black color for all lines
  const strokeColor = '#000000';

  return (
    <svg 
      width={width} 
      height={height} 
      className="sparkline-chart"
      style={{ 
        display: 'block',
        margin: '0 auto'
      }}
    >
      <path
        d={pathData}
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
      {/* Optional: Add dots for first and last points */}
      <circle
        cx={padding}
        cy={padding + chartHeight - ((firstValue - minValue) / range) * chartHeight}
        r="1"
        fill={strokeColor}
      />
      <circle
        cx={width - padding}
        cy={padding + chartHeight - ((lastValue - minValue) / range) * chartHeight}
        r="1"
        fill={strokeColor}
      />
    </svg>
  );
};

export default SparklineChart;